import type { PropsWithChildren } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ComponentsProvider } from '@looker/components';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { ErrorBoundary } from './components/error-boundary';
import { FeApiProvider } from './components/fe-api-provider';
import { GlobalError } from './components/global-error';
import { AuthLoading } from './components/layout/auth-loading';
import { LookerProvider } from './components/looker-provider';
import { PostHogProvider } from './posthog/provider';
import { AuthProvider } from './providers/auth';
import { LookerEnvProvider } from './providers/looker-env';
import { LookerSdk } from './providers/looker-sdk';
import { MeProvider } from './providers/me';
import { PageContextProvider } from './providers/page-context';
import { ToastProvider } from './providers/toast';
import { Auth0Provider } from './providers/vendors/auth0';
import { ReactQueryProvider } from './providers/vendors/react-query';

function VendorProviders({ children }: PropsWithChildren<{}>) {
  return (
    <PostHogProvider>
      <ToastProvider>
        <BrowserRouter>
          <ReactQueryProvider>
            <Auth0Provider>{children}</Auth0Provider>
            <ReactQueryDevtools initialIsOpen={false} />
          </ReactQueryProvider>
        </BrowserRouter>
      </ToastProvider>
    </PostHogProvider>
  );
}

function InternalProviders({ children }: PropsWithChildren<{}>) {
  return (
    <ErrorBoundary fallback={GlobalError}>
      <AuthProvider loadingComponent={<AuthLoading />}>
        <MeProvider loadingComponent={<></>}>
          <LookerSdk>
            <LookerEnvProvider>
              <ComponentsProvider
                themeCustomizations={{
                  colors: { key: '#36C799' },
                }}
                loadGoogleFonts
              >
                <FeApiProvider>
                  <PageContextProvider>
                    <LookerProvider>{children}</LookerProvider>
                  </PageContextProvider>
                </FeApiProvider>
              </ComponentsProvider>
            </LookerEnvProvider>
          </LookerSdk>
        </MeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export function Providers({ children }: PropsWithChildren<{}>) {
  return (
    <VendorProviders>
      <InternalProviders>{children}</InternalProviders>
    </VendorProviders>
  );
}
